import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const FIELD_TYPES = [
  { v: "text", l: "Metin" },
  { v: "tel", l: "Telefon" },
  { v: "email", l: "E-posta" },
  { v: "date", l: "Tarih" },
  { v: "textarea", l: "Uzun Metin" },
  { v: "select", l: "Açılır Liste" },
  { v: "number", l: "Sayı" },
];

const empty = {
  key: "", title: "", short_description: "", description: "",
  icon: "Shield", image_url: "", order: 0, active: true, category: "sigorta", form_fields: [],
};

export default function ProductsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/products/all").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, form_fields: [] }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p, form_fields: p.form_fields || [] }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        delete payload.key;
        await api.put(`/products/${editing.key}`, payload);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/products", form);
        toast.success("Ürün eklendi");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (key) => {
    if (!confirm("Ürünü silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/products/${key}`);
      toast.success("Ürün silindi");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addField = () => setForm((f) => ({
    ...f, form_fields: [...(f.form_fields || []), { name: `alan_${(f.form_fields || []).length + 1}`, label: "Yeni Alan", type: "text", required: false, options: [] }]
  }));
  const updateField = (idx, k, v) => setForm((f) => {
    const arr = [...f.form_fields]; arr[idx] = { ...arr[idx], [k]: v }; return { ...f, form_fields: arr };
  });
  const removeField = (idx) => setForm((f) => ({ ...f, form_fields: f.form_fields.filter((_, i) => i !== idx) }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl">Ürünler</h1>
          <p className="text-zinc-400 text-sm mt-1">Sitede gösterilen sigorta ürünlerini ve talep formu alanlarını düzenleyin.</p>
        </div>
        <Button onClick={openNew} className="asg-btn-primary rounded-full border-0" data-testid="product-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Yeni Ürün
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {rows.map((p) => (
          <div key={p.key} className="asg-card rounded-2xl overflow-hidden" data-testid={`product-card-admin-${p.key}`}>
            <div className="h-32 relative overflow-hidden">
              <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
              <span className={`absolute top-3 right-3 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-500/20 text-zinc-300"}`}>
                {p.active ? "Aktif" : "Pasif"}
              </span>
            </div>
            <div className="p-5">
              <div className="font-heading text-lg">{p.title}</div>
              <div className="text-xs text-zinc-500 mt-0.5">Anahtar: {p.key} • {p.category === "turbo_asist" ? "Turbo Asist" : "Sigorta"} • {p.form_fields?.length || 0} alan</div>
              <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.short_description}</p>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" onClick={() => openEdit(p)} className="border-white/10 text-white bg-transparent hover:bg-white/5 h-8 text-xs" data-testid={`product-edit-${p.key}`}>
                  <Pencil className="w-3 h-3 mr-1.5" /> Düzenle
                </Button>
                <Button variant="outline" onClick={() => del(p.key)} className="border-white/10 text-red-400 bg-transparent hover:bg-red-500/10 h-8 text-xs" data-testid={`product-delete-${p.key}`}>
                  <Trash2 className="w-3 h-3 mr-1.5" /> Sil
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--asg-surface)] border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Ürün Düzenle" : "Yeni Ürün"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2"><Label>Ürün Anahtarı (URL)</Label>
                <Input value={form.key} disabled={!!editing} onChange={(e) => set("key", e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))} className="bg-[color:var(--asg-bg)] border-white/10 disabled:opacity-60" data-testid="product-form-key" />
              </div>
              <div className="grid gap-2"><Label>Başlık</Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" data-testid="product-form-title" />
              </div>
              <div className="grid gap-2"><Label>İkon (lucide adı)</Label>
                <Input value={form.icon} onChange={(e) => set("icon", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" placeholder="Car, Home, Plane, HeartPulse, Shield, Truck..." />
              </div>
              <div className="grid gap-2"><Label>Kategori (Ana Sayfa Sekmesi)</Label>
                <Select value={form.category || "sigorta"} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className="bg-[color:var(--asg-bg)] border-white/10" data-testid="product-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-[color:var(--asg-border)]">
                    <SelectItem value="sigorta">Sigorta &amp; Kasko</SelectItem>
                    <SelectItem value="turbo_asist">Turbo Asist (Yol Yardım)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2"><Label>Sıra</Label>
                <Input type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className="bg-[color:var(--asg-bg)] border-white/10" />
              </div>
              <div className="sm:col-span-2 grid gap-2"><Label>Görsel URL</Label>
                <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" />
              </div>
              <div className="sm:col-span-2 grid gap-2"><Label>Kısa Açıklama</Label>
                <Textarea rows={2} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" />
              </div>
              <div className="sm:col-span-2 grid gap-2"><Label>Detaylı Açıklama</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="product-form-active" />
                <Label>Aktif</Label>
              </div>
            </div>

            <div className="mt-2 border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-base">Talep Formu Alanları</Label>
                  <p className="text-xs text-zinc-500">Bu ürüne özel olarak talep formunda gösterilecek alanlar.</p>
                </div>
                <Button variant="outline" size="sm" onClick={addField} className="border-white/10 bg-transparent text-white hover:bg-white/5" data-testid="product-add-field">
                  <Plus className="w-3 h-3 mr-1.5" /> Alan Ekle
                </Button>
              </div>
              <div className="grid gap-3">
                {(form.form_fields || []).map((f, idx) => (
                  <div key={idx} className="rounded-xl bg-white/5 p-4 grid sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-3 grid gap-1">
                      <Label className="text-xs">Anahtar</Label>
                      <Input value={f.name} onChange={(e) => updateField(idx, "name", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10 h-9" />
                    </div>
                    <div className="sm:col-span-4 grid gap-1">
                      <Label className="text-xs">Etiket</Label>
                      <Input value={f.label} onChange={(e) => updateField(idx, "label", e.target.value)} className="bg-[color:var(--asg-bg)] border-white/10 h-9" />
                    </div>
                    <div className="sm:col-span-2 grid gap-1">
                      <Label className="text-xs">Tip</Label>
                      <Select value={f.type} onValueChange={(v) => updateField(idx, "type", v)}>
                        <SelectTrigger className="bg-[color:var(--asg-bg)] border-white/10 h-9"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[color:var(--asg-surface)] border-white/10 text-white">
                          {FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2 flex items-end gap-2">
                      <Switch checked={f.required} onCheckedChange={(v) => updateField(idx, "required", v)} />
                      <Label className="text-xs">Zorunlu</Label>
                    </div>
                    <div className="sm:col-span-1 flex items-end justify-end">
                      <button onClick={() => removeField(idx)} className="text-zinc-400 hover:text-red-400">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {f.type === "select" && (
                      <div className="sm:col-span-12 grid gap-1">
                        <Label className="text-xs">Seçenekler (virgülle ayırın)</Label>
                        <Input
                          value={(f.options || []).join(", ")}
                          onChange={(e) => updateField(idx, "options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                          className="bg-[color:var(--asg-bg)] border-white/10 h-9"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-300 hover:text-white">Vazgeç</Button>
            <Button onClick={save} className="asg-btn-primary rounded-full border-0" data-testid="product-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
